import React, { useMemo, useState } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useControllerToDToE } from '../../../hooks/analytics/useControllerReports.ts';
import { WidgetCard } from './shared.tsx';
import { CHART_CATEGORICAL, CHART_LEGEND_STYLE, CHART_TOOLTIP_STYLE, SEMANTIC } from '../theme.ts';

const COLORS: Record<string, string> = {
  tod: CHART_CATEGORICAL[0],
  toe: CHART_CATEGORICAL[2],
  other: SEMANTIC.neutral.chart,
};

type TestTypeFilter = 'all' | 'tod' | 'toe';

/** Report #26 — ToD vs ToE Breakdown. Donut chart of run counts by test_type, with a radio filter for the table below. */
export const ToDToEChart: React.FC<{ controlIdFilter?: Set<string> | null }> = ({ controlIdFilter }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useControllerToDToE();
  const [filter, setFilter] = useState<TestTypeFilter>('all');

  const scoped = useMemo(
    () => (controlIdFilter ? (data ?? []).filter((r) => controlIdFilter.has(r.control_id)) : data ?? []),
    [data, controlIdFilter]
  );

  const donutData = useMemo(() => {
    // Group by the canonicalized (lower-cased/trimmed) test_type — the
    // underlying `test_runs.test_type` column is a free-form varchar(20), so
    // stray casing (e.g. a seeded "TOE" alongside "toe") must not fragment
    // the aggregation into a spurious extra slice with the wrong color.
    const totals = new Map<string, number>();
    scoped.forEach((row) => {
      const key = (row.test_type ?? 'other').trim().toLowerCase() || 'other';
      const runCount = Number(row.run_count) || 0;
      totals.set(key, (totals.get(key) ?? 0) + runCount);
    });
    return Array.from(totals.entries())
      .filter(([, value]) => value > 0)
      .map(([type, count]) => ({ name: type.toUpperCase(), type, value: count }));
  }, [scoped]);

  const filteredRows = useMemo(() => {
    return filter === 'all' ? scoped : scoped.filter((r) => (r.test_type ?? '').trim().toLowerCase() === filter);
  }, [scoped, filter]);

  const totalRuns = donutData.reduce((sum, d) => sum + d.value, 0);

  return (
    <WidgetCard
      title="ToD vs ToE Breakdown"
      description="Test of Design vs. Test of Effectiveness run mix"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load ToD/ToE breakdown.'}
      isEmpty={!isLoading && !isError && donutData.length === 0}
      emptyMessage="No test runs recorded yet."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="flex items-center gap-2 mb-3 text-sm">
        {(['all', 'tod', 'toe'] as TestTypeFilter[]).map((option) => (
          <label key={option} className="inline-flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="radio"
              name="tod-toe-filter"
              className="border-gray-300"
              checked={filter === option}
              onChange={() => setFilter(option)}
            />
            <span className="capitalize">{option === 'all' ? 'All' : option.toUpperCase()}</span>
          </label>
        ))}
      </div>
      {/* Donut fix (feedback round 2026-08-23): innerRadius/outerRadius were
          fixed pixel values (50/80 = 160px across), which overflowed/mis-laid-
          out whenever the container was narrower than that — exactly what
          happens in the self-serve edit-mode grid, where the widget can be
          dragged down to a few dozen px wide, and the fixed-radius pie then
          renders fragmented, detached arc pieces instead of a ring.
          Percentage-based radii scale with the actual container at any size.
          Also: paddingAngle=3 combined with a 2px white stroke drawn around
          *both* radial edges of each ~50%-share slice read as two separate
          "detached" half-arcs rather than a joined ring; a smaller padding
          angle + thinner stroke keeps the two slices visually joined while
          still separating them. `debounce` smooths rapid resize during
          drag/resize in edit mode instead of re-rendering on every pixel. */}
      <div className="h-56 min-w-[120px] min-h-[120px]">
        <ResponsiveContainer width="100%" height="100%" debounce={50}>
          <PieChart>
            <Pie
              data={donutData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="80%"
              paddingAngle={donutData.length > 1 ? 1 : 0}
              isAnimationActive={false}
            >
              {donutData.map((entry) => (
                <Cell key={entry.type} fill={COLORS[entry.type] ?? COLORS.other} stroke="#fff" strokeWidth={1} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number, name: string) => [`${value} runs`, name]}
              contentStyle={CHART_TOOLTIP_STYLE}
            />
            <Legend wrapperStyle={CHART_LEGEND_STYLE} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <p className="text-center text-xs text-slate-500 -mt-2 mb-3">{totalRuns} total runs</p>
      <div className="analytics-scroll border-t border-slate-100 pt-3 max-h-64 overflow-y-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <th className="pb-2">Control</th>
              <th className="pb-2">Type</th>
              <th className="pb-2 text-right">Runs</th>
              <th className="pb-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredRows.map((row, idx) => (
              <tr key={`${row.control_id}-${row.test_type ?? 'na'}-${idx}`} className="hover:bg-slate-50">
                <td className="py-1.5 pr-2 text-slate-900">
                  {row.control_id} — {row.title}
                </td>
                <td className="py-1.5 pr-2 uppercase text-slate-500">{row.test_type ?? '—'}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums text-slate-600">{row.run_count}</td>
                <td className="py-1.5 text-slate-500">{row.conclusion ?? row.overall_status ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </WidgetCard>
  );
};

export default ToDToEChart;
